/**
 * iter v33 r36 · BatchTrayPill — pill flottante in basso-sinistra che
 * mostra il progresso di Migliora tutte SENZA coprire il menu in alto.
 *
 * User request: «rimane il pop-up alto che non mi permette poi di
 * selezionare il menu a sinistra. Lo lascerei magari un pop-up che non
 * dia fastidio o comunque si possa non dico cancellare, ma che non dia
 * fastidio nella schermata».
 *
 * Design:
 * - Posizione: fixed, bottom (sopra la BottomTabBar iOS, safe-area
 *   aware), left aligned. NON copre l'header.
 * - Contenuto: badge "N/M · titolo annuncio", tap → naviga all'annuncio.
 * - Bottone X per NASCONDERE (il batch continua sul server); la pill
 *   riappare al prossimo poll se il batch e' ancora attivo — quindi
 *   "nascondere" ha effetto solo mentre l'utente sta usando l'app,
 *   torna se vuoi rivedere lo stato.
 * - Auto-dismiss: quando il tracker riceve la notifica di completamento,
 *   la pill sparisce automaticamente (il global tracker chiama il
 *   registerOnComplete callback).
 */
import { useMemo, useState } from "react";
import { X, Spinner } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useBatchEnhanceTracker } from "@/context/BatchEnhanceTracker";
import { isNative } from "@/lib/native";

export default function BatchTrayPill() {
  const { activeBatches } = useBatchEnhanceTracker();
  const nav = useNavigate();
  // Job che l'utente ha nascosto in questa sessione (id set).
  const [hiddenIds, setHiddenIds] = useState(() => new Set());

  const visible = useMemo(
    () => (activeBatches || []).filter((j) => !hiddenIds.has(j.job_id)),
    [activeBatches, hiddenIds],
  );

  if (!visible.length) return null;

  // Se ho piu' batch attivi mostro solo il primo (piu' recente = created_at desc).
  const job = visible[0];
  const done = Number(job.done || 0);
  const total = Number(job.total || 1);
  const errors = Number(job.errors || 0);
  const inProgress = Number(job.in_progress || 0);
  const title = (job.property_title || "").slice(0, 30) || "annuncio";

  return (
    <div
      className="fixed left-4 z-[55] max-w-[calc(100vw-2rem)] rounded-full border border-[#1C1C1A]/12 bg-white/95 backdrop-blur-md shadow-lg shadow-black/10 overflow-hidden flex items-center"
      data-testid="batch-tray-pill"
      style={{
        // Sopra la bottom tab-bar iOS (80px + safe-area) o al margine 16px
        // sul web dove non c'e' la barra.
        bottom: isNative
          ? "calc(80px + env(safe-area-inset-bottom) + 8px)"
          : "16px",
      }}
    >
      <button
        type="button"
        onClick={() => nav(`/dashboard/property/${job.property_id}?tab=foto`)}
        className="flex items-center gap-2 pl-3.5 pr-2 py-2 text-[#1C1C1A] hover:bg-[#F1F4ED] transition-colors"
        data-testid="batch-tray-pill-goto"
      >
        <Spinner size={14} className="animate-spin text-[#7C8B6E]" />
        <span className="text-[12.5px] font-semibold tabular-nums">
          {done}/{total}
        </span>
        <span className="text-[11.5px] text-[#6B6A65] max-w-[160px] truncate">
          {title}
          {inProgress > 0 ? ` · ${inProgress} in corso` : ""}
          {errors > 0 ? ` · ${errors} err` : ""}
        </span>
      </button>
      <button
        type="button"
        onClick={() => setHiddenIds((s) => new Set([...s, job.job_id]))}
        className="px-2 py-2 text-[#9A968B] hover:text-[#1C1C1A] hover:bg-[#EDEAE2] transition-colors"
        aria-label="Nascondi"
        data-testid="batch-tray-pill-hide"
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}
